import { Button, Center, HStack, Icon, Spinner, Text, VStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowLeft } from "react-icons/fi";
import { Navigate, Link as RouterLink } from "react-router-dom";
import { useGetAuthenticatedPortalSourcingEventQuery } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { isNoAccessError } from "../../common/errors/isNoAccessError";
import { ListLoadError } from "../../common/ListLoadError";
import { setOrganizationId } from "../../common/local-storage/selected-organization";
import { urls } from "../../urls";
import {
  organizationsThatCanOpen,
  probeBuyerEventAccess,
  type EventAccessProbe,
  type OpenableOrganizations,
} from "./organizationsThatCanOpen";

export const EventUnavailable = ({
  eventId,
  probe = probeBuyerEventAccess,
}: {
  eventId: string;
  probe?: EventAccessProbe;
}) => {
  const { t } = useTranslation();
  const { organizations, selectedOrg } = useLoggedInWithOrgContextState();
  const asSupplier = useGetAuthenticatedPortalSourcingEventQuery({ eventId });
  const [lookup, setLookup] = useState<OpenableOrganizations | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLookup(null);
    const others = organizations.filter((org) => org.id !== selectedOrg.id);
    void organizationsThatCanOpen(eventId, others, probe).then((found) => {
      if (cancelled) return;
      if (found.openable.length === 1) openAs(found.openable[0].id);
      else setLookup(found);
    });
    return () => {
      cancelled = true;
    };
  }, [eventId, organizations, selectedOrg.id, probe, attempt]);

  if (lookup == null || asSupplier.isLoading) {
    return (
      <Center py="20">
        <Spinner color="smPrimary" />
      </Center>
    );
  }

  const { openable, anyProbeFailed } = lookup;

  const supplierLookupFailed =
    asSupplier.isError &&
    !isNoAccessError(asSupplier.error);

  if (openable.length === 0 && (anyProbeFailed || supplierLookupFailed)) {
    return (
      <EventLoadFailed
        isRetrying={asSupplier.isFetching}
        onRetry={() => {
          setAttempt((count) => count + 1);
          if (supplierLookupFailed) void asSupplier.refetch();
        }}
      />
    );
  }

  if (openable.length === 0 && asSupplier.data) {
    return <Navigate to={urls.app.supplier.procurement.go(eventId)} replace />;
  }

  return (
    <Center py="20">
      <VStack spacing="4">
        <Text color="smError">{t("Could not load this sourcing event.")}</Text>
        {openable.length > 0 && (
          <>
            <Text color="smMuted">{t("It belongs to another of your organizations. Open it as:")}</Text>
            <HStack>
              {openable.map((org) => (
                <Button key={org.id} variant="outline" onClick={() => openAs(org.id)}>
                  {org.name}
                </Button>
              ))}
            </HStack>
          </>
        )}
        <BackToProcurements />
      </VStack>
    </Center>
  );
};

export const EventLoadFailed = ({ onRetry, isRetrying = false }: { onRetry: () => void; isRetrying?: boolean }) => {
  const { t } = useTranslation();
  return (
    <Center py="10">
      <VStack spacing="4">
        <ListLoadError
          message={t("This procurement could not be loaded right now.")}
          onRetry={onRetry}
          isRetrying={isRetrying}
        />
        <BackToProcurements />
      </VStack>
    </Center>
  );
};

const BackToProcurements = () => {
  const { t } = useTranslation();
  return (
    <Button
      as={RouterLink}
      to={urls.app.sourcingEvents.viewAll}
      variant="outline"
      size="sm"
      leftIcon={<Icon as={FiArrowLeft} />}
    >
      {t("Back to procurements")}
    </Button>
  );
};

const openAs = (organizationId: string) => {
  setOrganizationId(organizationId);
  window.location.reload();
};
